/**
 * SecretariatLayout — Sidebar Secrétariat au style codevoiture (Phase 30).
 * Palette orange/ambre pour distinguer du back-office admin (rouge).
 */
import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Calendar, Users, GraduationCap, Package, BarChart3, LogOut, ArrowLeft, ExternalLink, Building2 } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { RefreshButton } from './RefreshButton';
import api from '../api';

const NAV = [
  { to: '/secretariat/dashboard', label: 'Tableau de bord', icon: Building2, exact: true, testid: 'sec-nav-dashboard' },
  { to: '/secretariat/planning', label: 'Planning', icon: Calendar, testid: 'sec-nav-planning' },
  { to: '/secretariat/eleves', label: 'Élèves', icon: Users, testid: 'sec-nav-eleves', badgeKey: 'eleves' },
  { to: '/secretariat/enseignants', label: 'Enseignants', icon: GraduationCap, testid: 'sec-nav-enseignants', badgeKey: 'enseignants' },
  { to: '/secretariat/formules', label: 'Formules & tarifs', icon: Package, testid: 'sec-nav-formules' },
  { to: '/secretariat/stats', label: 'Import & stats', icon: BarChart3, testid: 'sec-nav-stats' },
];

export default function SecretariatLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useApp() || {};
  const [counts, setCounts] = useState({ eleves: 0, enseignants: 0 });

  useEffect(() => {
    (async () => {
      try {
        const s = await api.boGetSecretariatStats();
        setCounts({ eleves: s?.eleves || 0, enseignants: s?.enseignants || 0 });
      } catch (_) { /* silent */ }
    })();
  }, [pathname]);

  const onLogout = () => { if (logout) logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex flex-col lg:flex-row" data-testid="secretariat-layout">
      {/* ===== SIDEBAR ===== */}
      <aside className="lg:w-60 lg:min-h-screen shrink-0 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0d0d0e] flex lg:flex-col">
        <div className="hidden lg:flex items-center gap-2.5 px-5 h-16 border-b border-white/10">
          <div className="w-9 h-9 rounded-xl bg-amber-500 text-white flex items-center justify-center shadow-lg shadow-amber-900/40">
            <Building2 size={16} />
          </div>
          <div>
            <div className="text-[9.5px] uppercase tracking-[0.22em] text-amber-400 font-bold">code2roues.fr</div>
            <div className="text-white text-[14px] font-semibold leading-tight">Secrétariat</div>
          </div>
        </div>
        <nav className="flex lg:flex-col gap-1 p-2 lg:p-3 overflow-x-auto lg:overflow-visible flex-1 items-center lg:items-stretch" data-testid="sec-sidebar-nav">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = n.exact ? pathname === n.to : pathname.startsWith(n.to);
            const count = n.badgeKey ? counts[n.badgeKey] : 0;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold whitespace-nowrap transition ${
                  active ? 'bg-amber-500 text-white shadow-md shadow-amber-900/40' : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon size={15} className="shrink-0" />
                <span className="flex-1">{n.label}</span>
                {count > 0 && (
                  <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10.5px] font-bold inline-flex items-center justify-center ${active ? 'bg-white text-amber-600' : 'bg-amber-500 text-white'}`}>
                    {count}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="hidden lg:block p-3 border-t border-white/10 space-y-1">
          <Link to="/admin/dashboard" data-testid="sec-back-to-admin" className="flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold text-white/60 hover:bg-white/5 hover:text-white transition">
            <ArrowLeft size={14} /> Retour admin
          </Link>
          <Link to="/" data-testid="sec-back-to-site" className="flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold text-white/60 hover:bg-white/5 hover:text-white transition">
            <ExternalLink size={14} /> Retour à l&apos;accueil
          </Link>
          <button onClick={onLogout} data-testid="sec-logout" className="w-full flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold text-white/65 hover:bg-red-950/40 hover:text-red-300 transition">
            <LogOut size={14} /> Déconnexion
          </button>
          {user && <div className="px-3.5 pt-2 pb-1 text-white/35 text-[11px] truncate">{user.email}</div>}
        </div>
      </aside>

      {/* ===== CONTENU ===== */}
      <div className="flex-1 min-w-0">
        <header className="h-12 lg:h-16 border-b border-white/10 bg-[#0d0d0e]/60 backdrop-blur flex items-center justify-between px-4 lg:px-8">
          <div className="text-white/45 text-[12px] uppercase tracking-[0.2em] truncate">Gestion auto-école</div>
          <div className="flex items-center gap-2">
            <RefreshButton variant="secretariat" />
            <div className="hidden lg:block text-white/50 text-[12px]">{user?.name || 'Administrateur'}</div>
            <div className="flex items-center gap-2 lg:hidden">
              <Link to="/admin/dashboard" className="text-white/60 text-[11px] inline-flex items-center gap-1"><ArrowLeft size={12} /> Admin</Link>
              <button onClick={onLogout} className="text-red-300 text-[12px] inline-flex items-center gap-1"><LogOut size={12} /> Quitter</button>
            </div>
          </div>
        </header>
        <main className="px-4 lg:px-8 py-6 lg:py-8" data-testid="sec-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
